import { useEffect, useState, useMemo, useCallback, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Wallet.scss";
import {
  Col,
  Row,
  Typography,
  Tabs,
  Input,
  Checkbox,
  Card,
  List,
  message,
  Flex
} from "antd";
import { ArrowDownOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { formatCurrencyTwoDec, DateFormatter, downloadBlob, getDownloadSuccessMessage } from "../../helpers/helpers";
import axios from "../../config/httpConfig";
import DownloadFormatDropdown from "../../components/DownloadFormatDropdown/DownloadFormatDropdown";
import Deposit from "./Components/Deposit";
import Withdraw from "./Components/Withdraw";
import Loader from "../../components/CustomLoader/Loader";
import { filterOptions } from "../../constants/constants";
import { LIST_PAGINATION } from "../../constants/tableConfig";
import EntBreakdownTable from "./Components/EntBreakdownTable";
import { getAllEscrowDetails, getAccountTxnHistory } from "../../redux/slices/WalletsSlice"
const { Title, Text } = Typography;
const CheckboxGroup = Checkbox.Group;

const filterParamMapping = {
  1: "withdraw",
  2: "deposit",
  3: "internalTransfer",
  4: "dealRepayment",
};

const getApiParams = (filters) => {
  const params = {};
  Object.entries(filterParamMapping).forEach(([optionId, paramName]) => {
    params[paramName] = filters.includes(optionId);
  });
  return params;
};

const Wallet = () => {
  const dispatch = useDispatch();
  const [checkedFilters, setCheckedFilters] = useState([]);
  const { wallet, loading, txnHistoryData, txnHisLoading } = useSelector((state) => state?.wallets);
  const [searchText, setSearchText] = useState("");

  const refreshTxnHistory = useCallback(() => {
    dispatch(getAccountTxnHistory(getApiParams(checkedFilters)));
  }, [dispatch, checkedFilters]);

  useEffect(() => {
    refreshTxnHistory();
  }, [refreshTxnHistory]);

  const downloadTxnStatement = useCallback(async (format) => {
    try {
      const response = await axios.get("/api/business/downloadTxnStatement", {
        params: {
          format,
          ...getApiParams(checkedFilters)
        },
        responseType: "blob",
      });
      const fileName = "Transaction Summary";
      const downloadedFileName = `${fileName}.${format === "pdf" ? "pdf" : "xlsx"}`;
      downloadBlob(response, downloadedFileName);
      message.success(getDownloadSuccessMessage(downloadedFileName));
    } catch (error) {
      console.error("Error downloading report:", error);
      message.error("Failed to download the report. Please try again later.");
    }
  }, [checkedFilters]);

  const onFilterChange = useCallback((list) => {
    setCheckedFilters(list);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value);
  }, []);

  const getTxnTitle = useCallback((item) => (
    <Text style={{ fontWeight: 500 }}>
      {item?.purpose === "withdraw"
        ? "Withdrawal"
        : item?.purpose === "deposit"
          ? "Deposit"
          : `${item?.purpose}`}
    </Text>
  ), []);

  const txnDesc = useCallback((date, txnId, isVerified) => (
    <Flex>
      <Text style={{ color: "#40566D", fontSize: 12 }}>
        {DateFormatter(date)} • Transaction ID: {txnId} •{" "}
        {isVerified === 'Failed' ? (
          <Text style={{ color: '#FF0000', fontSize: 12 }}><CloseCircleOutlined /> Failed</Text>
        ) : (
          <Text style={{ fontSize: 12 }}>{isVerified || 'Unknown'}</Text>
        )}
      </Text>
    </Flex>
  ), []);

  const renderTxnItem = useCallback((item) => (
    <List.Item>
      <List.Item.Meta
        avatar={
          <ArrowDownOutlined
            style={{
              color:
                item.transactionType === "debit"
                  ? "#101924"
                  : "#00874F",
            }}
            rotate={item.transactionType === "debit" ? 135 : 315}
          />
        }
        title={getTxnTitle(item)}
        description={txnDesc(
          item?.transactionDate,
          item?.appTransactionId,
          item?.transactionStatusId
        )}
      />
      <Text
        style={{
          color:
            item.transactionType === "debit" ? "#101924" : "#00874F",
        }}
        strong
      >
        {formatCurrencyTwoDec(item?.amount)}
      </Text>
    </List.Item>
  ), [getTxnTitle, txnDesc]);

  useEffect(() => {
    dispatch(getAllEscrowDetails());
  }, [dispatch]);

  const filteredEntDetails = useMemo(() => {
    if (!wallet?.entDetails) return [];
    return wallet.entDetails.filter((ent) =>
      ent?.entName?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [wallet?.entDetails, searchText]);

  const walletForTable = useMemo(
    () => (wallet ? { ...wallet, entDetails: filteredEntDetails } : null),
    [wallet, filteredEntDetails]
  );

  const filterCheckboxOptions = useMemo(
    () =>
      filterOptions.map((option) => ({
        label: option.optionLabel,
        value: option.optionId,
      })),
    []
  );

  const tabItems = useMemo(
    () => [
      {
        key: "1",
        label: "DEPOSIT",
        children: <Deposit escrowDetails={wallet?.escrowDetails} />,
      },
      {
        key: "2",
        label: "WITHDRAW",
        children: (
          <Withdraw
            balance={wallet?.balance}
            bankDetailsData={wallet?.accDetails}
            withdrawableAmt={wallet?.balance?.withdrawable}
            onTxnRefresh={refreshTxnHistory}
          />
        ),
      },
    ],
    [
      wallet?.escrowDetails,
      wallet?.balance,
      wallet?.accDetails,
      refreshTxnHistory,
    ]
  );

  const handleDownloadPdf = useCallback(
    () => downloadTxnStatement("pdf"),
    [downloadTxnStatement]
  );

  const handleDownloadCsv = useCallback(
    () => downloadTxnStatement("xlsx"),
    [downloadTxnStatement]
  );

  return (
    <>
      <Title level={3} style={{ margin: 0, fontSize: 24 }}>
        Wallets
      </Title>
      {loading ? (
        <Loader />
      ) : (
        <Row
          gutter={[32, 32]}
          style={{ height: "100%", paddingTop: 20, paddingBottom: 20 }}
        >
          <Col xs={24} lg={16}>
            <Row gutter={[0, 16]} align="middle" justify="space-between">
              <Col>
                <Title level={4} style={{ margin: 0 }}>
                  Enterprise Breakdown
                </Title>
              </Col>
              <Col>
                <Input
                  type="text"
                  placeholder="Search Enterprise"
                  style={{ width: 250 }}
                  value={searchText}
                  onChange={handleSearchChange}
                />
              </Col>
            </Row>

            <EntBreakdownTable wallet={walletForTable} />
            <div className="transaction-container" style={{ marginTop: "2rem" }}>
              <Flex align="center" justify="space-between">
                <Title level={4}>Transactions</Title>
                <DownloadFormatDropdown
                  onPdf={handleDownloadPdf}
                  onCsv={handleDownloadCsv}
                  trigger={
                    <Flex gap="small" align="center" style={{ cursor: "pointer" }}>
                      <ArrowDownOutlined style={{ color: "#40566D" }} />
                      <Text style={{ color: "#40566D", fontWeight: 500 }}>
                        Download History
                      </Text>
                    </Flex>
                  }
                />
              </Flex>

              <div
                className="transaction-filter-container"
                style={{ marginTop: "1rem", marginBottom: "1rem" }}
              >
                <Text style={{ color: "#40566D" }}>Filter By</Text>
                <CheckboxGroup
                  options={filterCheckboxOptions}
                  value={checkedFilters}
                  onChange={onFilterChange}
                  style={{ marginLeft: "1rem" }}
                />
              </div>

              <Card className="transactions-card">
                <List
                  itemLayout="horizontal"
                  loading={txnHisLoading}
                  dataSource={txnHistoryData}
                  pagination={LIST_PAGINATION}
                  renderItem={renderTxnItem}
                />
              </Card>
            </div>
          </Col>

          <Col xs={24} lg={8}>
            <div className="bank-deatils-card">
              <Tabs defaultActiveKey="1" items={tabItems} />
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default memo(Wallet);
